import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { hasSystemPermission, type SystemPermissionCan } from './systemUserPermissions';

type SystemPermissionContextValue = {
    /** Whether the current system user may perform `resource.action`. */
    can: SystemPermissionCan;
};

const SystemPermissionContext = createContext<SystemPermissionContextValue | null>(null);

/**
 * Provides permission checks for the current system user's role. Mount inside the
 * authenticated system layout so every system page can call {@link useSystemPermission}.
 */
export function SystemPermissionProvider({ roleId, children }: { roleId: string | undefined; children: ReactNode }) {
    const value = useMemo<SystemPermissionContextValue>(
        () => ({ can: (resource, action) => hasSystemPermission(roleId, resource, action) }),
        [roleId]
    );

    return <SystemPermissionContext.Provider value={value}>{children}</SystemPermissionContext.Provider>;
}

/**
 * Access permission checks for the current system user. Usage:
 * `const { can } = useSystemPermission(); ... can('systemUsers', 'create')`.
 */
export function useSystemPermission(): SystemPermissionContextValue {
    const context = useContext(SystemPermissionContext);
    if (!context) {
        throw new Error('useSystemPermission must be used within a SystemPermissionProvider');
    }
    return context;
}
